import { MONEY_PATTERN, gt, isNegative, isZero, lt, money } from '@miraki/money';
import type { BudgetView } from '@miraki/contracts';

/**
 * A-04's budget panel — BG-07, BG-08, BG-10 — as pure rules, so they can be read and tested
 * without a browser.
 *
 * **Nothing here computes a budget.** Every figure is the resolver's (`ConsignmentService.budget()`,
 * through `GET /retailers/:id/budget`). This module only COMPARES figures the response already
 * carries — never subtracts, never adds — so there is no second statement of BG-03 to drift from
 * the first. In particular "how far over" is deliberately not shown: it would be
 * `inUse.total − assigned`, a figure the API does not return, computed in the browser.
 */

/** The account's standing on its budget, from the response alone. */
export type Standing =
  /** BG-01 — no budget assigned. The normal starting state where budgets are set per retailer. */
  | 'NONE'
  /** BG-08 — in use is above the assigned figure. Allowed; the retailer cannot order until it falls. */
  | 'OVER'
  | 'OK';

export function standing(b: Pick<BudgetView, 'assigned' | 'inUse'>): Standing {
  const assigned = money(b.assigned);
  if (isZero(assigned)) return 'NONE';
  return gt(money(b.inUse.total), assigned) ? 'OVER' : 'OK';
}

/** BG-02 — the four components, in BG-02's order, each its own figure (BG-10). */
export const COMPONENTS = ['openOrders', 'inTransit', 'held', 'soldUnpaid'] as const;
export type Component = (typeof COMPONENTS)[number];

/**
 * BG-02 (d) and BG-06 — "sold, not yet paid" is a component only where the portal releases the
 * budget on PAYMENT. Read from `releaseOn`, the portal's setting, never from which portal it is
 * (D46). Where it releases on SALE the line is omitted rather than shown as a permanent zero.
 */
export const componentsFor = (releaseOn: BudgetView['releaseOn']): readonly Component[] =>
  releaseOn === 'PAYMENT' ? COMPONENTS : COMPONENTS.filter((c) => c !== 'soldUnpaid');

// ------------------------------------------------------------------ changing it

export type AmountProblem = 'REQUIRED' | 'FORMAT' | 'NEGATIVE' | null;

/**
 * The new amount, before it is sent. `zSetBudget` checks only the money pattern, which admits a
 * negative figure; a negative budget means nothing, so it is refused here and the hand-over asks
 * for the same rule on the contract. Zero is allowed: it is BG-08 at its limit, and how an
 * account's ordering is stopped by budget.
 */
export function amountProblem(raw: string): AmountProblem {
  const v = raw.trim();
  if (!v) return 'REQUIRED';
  if (!MONEY_PATTERN.test(v)) return 'FORMAT';
  if (isNegative(money(v)) && !isZero(money(v))) return 'NEGATIVE';
  return null;
}

/** BG-07 — `zSetBudget`'s reason bound, 3…300 after trimming. */
export const REASON_MIN = 3;
export const REASON_MAX = 300;
export const reasonOk = (raw: string) => raw.trim().length >= REASON_MIN && raw.trim().length <= REASON_MAX;

/**
 * BG-08 — a WARNING, never a block: the new figure is below what is already in use. This is how
 * Admin reduces the exposure of an account that is over, so refusing it would defeat the rule.
 */
export function belowInUse(amount: string, b: Pick<BudgetView, 'inUse'>): boolean {
  if (amountProblem(amount) !== null) return false;
  return lt(money(amount.trim()), money(b.inUse.total));
}

/**
 * A stored amount ("1000000.0000") as it is shown for editing: trailing fractional zeros dropped
 * ("1000000", "1250.5", "1250.25"). Text only: the value is never parsed to a number (NF-04), and
 * what is sent is whatever the field holds, which the API reads as the same figure.
 */
export function plainAmount(raw: string): string {
  const v = raw.trim();
  return /^-?\d+\.\d+$/.test(v) ? v.replace(/\.?0+$/, '') : v;
}

/** The amount as the API expects it: a plain decimal string, never a parsed number (NF-04). */
export const toWire = (raw: string) => raw.trim();

// ------------------------------------------------------- BG-04, the tolerance band

/**
 * Where the tolerance in force came from. Mirrors `ToleranceSource` on the API; the server is
 * the authority and this must not drift from it.
 */
export type ToleranceSource = 'RETAILER' | 'PORTAL' | 'PLATFORM_DEFAULT';
export interface Tolerance { pct: string; source: ToleranceSource }

export type TolerancePctProblem = 'FORMAT' | 'TOO_HIGH' | null;

/**
 * BG-04 — the tolerance box, before it is sent. The same rule as `zPercent`: a decimal
 * percentage, four places at most, not above 100.
 *
 * **Blank is not a problem, it is an instruction** — "clear this account's own figure and take
 * the portal's" — which is why it returns null for empty and the caller decides what to send.
 * `'0'` is a different instruction again: no tolerance for this account at all.
 */
export function tolerancePctProblem(raw: string): TolerancePctProblem {
  const v = raw.trim();
  if (!v) return null;
  if (!/^\d{1,3}(\.\d{1,4})?$/.test(v)) return 'FORMAT';
  // A bound, not arithmetic on the value: the figure itself never becomes a number (NF-04).
  if (Number(v) > 100) return 'TOO_HIGH';
  return null;
}

/**
 * What `tolerancePct` to put on the request, given the box and what the account has today.
 *
 * Three answers, and the difference between them is the whole point of the field:
 *   `undefined` — the box is unchanged, so say nothing and leave the account as it is.
 *   `null`      — the box was emptied: clear the override, take the portal's figure.
 *   a string    — this account's own figure, `'0'` included.
 */
export function tolerancePctFor(raw: string, current: Tolerance): string | null | undefined {
  const v = raw.trim();
  const ownsOne = current.source === 'RETAILER';
  if (!v) return ownsOne ? null : undefined;
  // Unchanged if it is the same figure the account already holds its own copy of.
  if (ownsOne && samePct(v, current.pct)) return undefined;
  return v;
}

/** "5" and "5.0000" are one percentage. The same comparison `sameBudget` makes for an amount. */
export const samePct = (a: string, b: string) => plainAmount(a.trim()) === plainAmount(b.trim());
